import { type FetchSocialMarketingBannersV2ParamsPageDisplay, pxToDP } from '@capsicum/common';
import { SkeletonItem, Text, useRTL, useWindowSize } from '@capsicum/common-web';
import { MARKETING_BANNER_SLIDE_INTERVAL, useMarketingBannerCarousel } from '@capsicum/features/feeds/shared';
import { useCallback, useMemo } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Autoplay } from 'swiper/modules';
import { Swiper, type SwiperProps, SwiperSlide } from 'swiper/react';
import { match, P } from 'ts-pattern';

import { MarketingBanner } from '../marketing-banner/MarketingBanner';

type MarketingBannerCarouselProps = {
	gutterBottom?: number;
	height?: number;
	page: FetchSocialMarketingBannersV2ParamsPageDisplay;
	pageHorizontalSpacing?: number;
};

export const MarketingBannerCarousel = ({
	gutterBottom = 0,
	height = 158,
	page,
	pageHorizontalSpacing: pageHorizontalSpacingProps = 0
}: MarketingBannerCarouselProps) => {
	const { isRTL } = useRTL();

	const {
		dimensions: { width }
	} = useWindowSize();

	const {
		breakpoints: {
			points: {
				tabletSmallDesktop: { maxScreenWidth }
			}
		}
	} = useTheme();

	const { activeSlide, isLoading, isVisible, marketingBanners, onActiveSlideChange, totalAmount } =
		useMarketingBannerCarousel({ page });

	const isPaginatable = totalAmount > 1;
	const pageHorizontalSpacing = width > maxScreenWidth ? 0 : pageHorizontalSpacingProps;

	const slides = useMemo(
		() =>
			marketingBanners.map(item => (
				<S.StyledSwiperSlide $pageHorizontalSpacing={pageHorizontalSpacing} key={item.id}>
					<MarketingBanner
						action={item.action}
						body={item.body}
						cta={item.cta}
						forceActionContainerVisibility={isPaginatable}
						header={item.header}
						height={marketingBanners.length === 1 ? undefined : height}
						image={item.image}
						indentAction={isPaginatable}
						subtitle={item.subtitle}
						testID={`marketingBannerCarousel-banner-${item.id}`}
						theme={item.theme}
					/>
				</S.StyledSwiperSlide>
			)),
		[height, isPaginatable, marketingBanners, pageHorizontalSpacing]
	);

	const [swiperModules, swiperAutoplay] = useMemo(
		() => [[Autoplay], { delay: MARKETING_BANNER_SLIDE_INTERVAL, disableOnInteraction: false }],
		[]
	);

	const onSlideChange = useCallback<Exclude<SwiperProps['onSlideChange'], undefined>>(
		swiper => onActiveSlideChange(swiper.realIndex),
		[onActiveSlideChange]
	);

	return match({
		isLoading,
		isVisible,
		totalAmount
	})
		.with(
			{
				isLoading: true,
				isVisible: true
			},
			() => (
				<S.Wrapper $gutterBottom={gutterBottom}>
					<SkeletonItem height={height} width="100%" />
				</S.Wrapper>
			)
		)
		.with(
			{
				isVisible: true,
				totalAmount: P.when(totalAmount => totalAmount > 0)
			},
			() => (
				<S.Wrapper $gutterBottom={gutterBottom}>
					<S.Content $pageHorizontalSpacing={pageHorizontalSpacing}>
						<Swiper
							allowTouchMove
							autoplay={swiperAutoplay}
							loop
							modules={swiperModules}
							spaceBetween={20 - pageHorizontalSpacing}
							onSlideChange={onSlideChange}
						>
							{slides}
						</Swiper>

						{isPaginatable && (
							<S.Pagination
								$isRTL={isRTL}
								$pageHorizontalSpacing={pageHorizontalSpacing}
								variant="caption1"
							>
								{`${activeSlide + 1}/${totalAmount}`}
							</S.Pagination>
						)}
					</S.Content>
				</S.Wrapper>
			)
		)
		.otherwise(() => null);
};

namespace S {
	export const Wrapper = styled.div<{ $gutterBottom: number }>`
		width: 100%;
		margin-bottom: ${({ $gutterBottom }) => $gutterBottom}px;
	`;

	export const Content = styled.div<{ $pageHorizontalSpacing: number }>`
		position: relative;
		margin-left: -${({ $pageHorizontalSpacing }) => $pageHorizontalSpacing}px;
		margin-right: -${({ $pageHorizontalSpacing }) => $pageHorizontalSpacing}px;
	`;

	export const StyledSwiperSlide = styled(SwiperSlide)<{ $pageHorizontalSpacing: number }>`
		box-sizing: border-box;
		padding-left: ${({ $pageHorizontalSpacing }) => $pageHorizontalSpacing}px;
		padding-right: ${({ $pageHorizontalSpacing }) => $pageHorizontalSpacing}px;
	`;

	export const Pagination = styled(Text)<{ $isRTL: boolean; $pageHorizontalSpacing: number }>`
		z-index: 10;
		position: absolute;
		${({ theme }) => theme.border.radius.circle};
		bottom: ${({ theme }) => theme.spacings.xsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		padding: ${({ theme }) => theme.spacings.xxxsmall} ${({ theme }) => theme.spacings.xxsmall};

		${({ $isRTL, $pageHorizontalSpacing, theme }) =>
			$isRTL
				? css`
						right: ${pxToDP(theme.spacings.xsmall) + $pageHorizontalSpacing}px;
					`
				: css`
						left: ${pxToDP(theme.spacings.xsmall) + $pageHorizontalSpacing}px;
					`}
	`;
}
